<template>
    <div class="container-fluid">
        <!-- LOADING -->
        <Loading v-if="!loaded"></Loading>
        <!-- LOADED -->
        <div v-else>
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>{{ $t('inventory') }}</h3>
            </div>
            <!-- NAVIGATION BAR -->
            <div class="d-flex flex-row justify-content-between mt-5 mb-5">
                <!-- TAB: INVENTORY -->
                <div class="d-flex flex-column" :style="{ width: '49%' }">
                    <button
                        :class="tabs.inventory ? 'btn btn-dark' : 'btn btn-light'"
                        v-on:click="switchTab('inventory')"
                    >
                        {{ $t('inventory') }}
                    </button>
                </div>
                <!-- TAB: DRUGSAFE -->
                <div class="d-flex flex-column" :style="{ width: '49%' }">
                    <button
                        :class="tabs.drugsafe ? 'btn btn-dark' : 'btn btn-light'"
                        v-on:click="switchTab('drugsafe')"
                    >
                        {{ $t('drugsafe') }}
                    </button>
                </div>
            </div>
            <!-- TAB CONTENT: INVENTORY -->
            <div v-if="tabs.inventory">
                <!-- FILTER: MEDICATION VNR / NAME -->
                <div class="form-group row mt-4">
                    <div class="col col-sm-8 col-md-6 col-lg-5 col-xl-4">
                        <label for="name">{{ `${$t('filter')} (${$t('vnr')} / ${$t('name').toLowerCase()})` }}</label>
                        <input type="text" id="name" class="form-control" v-model="searchName" />
                    </div>
                </div>
                <!-- INVENTORY TABLE -->
                <b-table class="mt-5" triped hover :fields="tableFields" :items="filteredInventory" sort-by="name">
                    <template #head(amount)="data">
                        <div v-b-tooltip.hover:title="$t('packages_pieces_tooltip')">{{ data.label }}</div>
                    </template>
                    <template #cell(name)="data">
                        {{ data.item.name }} {{ data.item.company }} {{ data.item.dosage }}
                        {{ data.item.package_size }} {{ data.item.form }} {{ data.item.dosage_form }}
                    </template>
                    <template #cell(amount)="data">
                        <div v-b-tooltip.hover.topleft.v-danger.html :title="getLowInvAlertTooltip(data.item.vnr)">
                            <span>{{ getDisplayAmount(data.item.amount, data.item.package_size) }}</span>
                            <span
                                v-if="
                                    vnrInventory[data.item.vnr].inventory <= vnrInventory[data.item.vnr].lowInvThreshold
                                "
                                :style="{ color: alertColor }"
                            >
                                {{ ' (!)' }}
                            </span>
                        </div>
                    </template>
                    <template #cell(last_put_in)="data">
                        {{ getLocalDate(data.item.last_put_in, $i18n.locale, true, true) }}
                    </template>
                    <template #cell(expiry_date)="data">
                        <div v-b-tooltip.hover.topleft.v-danger :title="data.item.expiry_alert ? expiryAlertText : ''">
                            <span>
                                {{ getLocalDate(data.item.expiry_date, $i18n.locale, false, false) }}
                            </span>
                            <span v-if="data.item.expiry_alert" :style="{ color: alertColor }">
                                {{ ' (!)' }}
                            </span>
                        </div>
                    </template>
                </b-table>
            </div>
            <!-- TAB CONTENT: DRUGSAFE -->
            <div v-if="tabs.drugsafe">
                <div class="row">
                    <div class="col-6">
                        <div class="safe"
                             v-for="(box, idx) in pharmacy.box"
                             v-bind:key="idx">
                            <ul class="door" v-if="idx === 0">
                                <li class="panel screen" v-bind:key="'0-0'" :id="'0-0'" v-on:click="chooseLocker('0-0')">
                                    <span></span>
                                </li>
                                <li
                                    v-for="(locker, index) in box"
                                    v-bind:key="idx + '-' + (index + 1)"
                                    class="panel"
                                    :class="'type' + locker"
                                    :id="idx + '-' + (index + 1)"
                                    v-on:click="chooseLocker(idx + '-' + (parseInt(index) + 1))"
                                >
                                    <span></span>
                                </li>
                            </ul>

                            <ul class="door" v-if="idx > 0">
                                <li
                                    v-for="(locker, index) in box"
                                    v-bind:key="idx + '-' + index"
                                    class="panel"
                                    :class="'type' + locker"
                                    :id="idx + '-' + index"
                                    v-on:click="chooseLocker(idx + '-' + index)"
                                >
                                    <span></span>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="col-6">
                        <table class="table table-borderless table-hover table-striped">
                            <thead>
                                <tr>
                                    <th>{{ $t('name') }}</th>
                                    <th>{{ $t('vnr') }}</th>
                                    <th>{{ `${$t('amount')} [${$t('packages_pieces_short')}]` }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in lockerInventory" v-bind:key="item.id">
                                    <td>
                                        {{ item.name }} {{ item.company }} {{ item.dosage }} {{ item.package_size }}
                                        {{ item.form }} {{ item.dosage_form }}
                                    </td>
                                    <td>{{ item.vnr }}</td>
                                    <td>
                                        <div
                                            v-b-tooltip.hover.topleft.v-danger.html
                                            :title="getLowInvAlertTooltip(item.vnr)"
                                        >
                                            <span>{{ getDisplayAmount(item.amount, item.package_size) }}</span>
                                            <span
                                                v-if="
                                                    vnrInventory[item.vnr].inventory <=
                                                        vnrInventory[item.vnr].lowInvThreshold
                                                "
                                                :style="{ color: alertColor }"
                                            >
                                                {{ ' (!)' }}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Loading from '@/views/partial/Loading';
import { mapActions, mapState } from 'vuex';
import { getPharmacy, handleAxiosError } from '@/services/axiosService';
import { getLocalDate } from '@/services/dateService';
import { getMedDisplayAmount } from '@/services/medicationService';
import { STORAGE_URL } from '@/services/storageService';

export default {
    components: {
        Loading
    },
    data() {
        return {
            alertColor: 'FireBrick',
            bruteInventory: {},
            getMedDisplayAmount,
            getLocalDate,
            inventory: [],
            loaded: false,
            pharmacy: null,
            prev_locker: null,
            searchName: '',
            STORAGE_URL,
            success: false,
            tabs: {
                inventory: true,
                drugsafe: false
            },
            vnrInventory: {}
        };
    },
    async created() {
        // get pharmacy info
        await this.getPharmacy();
        this.loaded = true;
    },
    computed: {
        ...mapState('account', ['user']),
        expiryAlertText() {
            return `${this.$t('alert_expires_in_less_than')} ${
                this.pharmacy.config.expiry_alert.time_value
            }\xa0${this.$t(this.pharmacy.config.expiry_alert.time_unit)}`;
        },
        filteredInventory() {
            let result = [],
                key;

            for (key in this.bruteInventory) {
                if (
                    this.bruteInventory.hasOwnProperty(key) &&
                    ((this.bruteInventory[key]['vnr'] !== null &&
                        this.bruteInventory[key]['vnr']
                            .toString()
                            .toLowerCase()
                            .indexOf(this.searchName.toLowerCase()) > -1) ||
                        (this.bruteInventory[key]['name'] !== null &&
                            this.bruteInventory[key]['name'].toLowerCase().indexOf(this.searchName.toLowerCase()) > -1))
                ) {
                    result.push(this.bruteInventory[key]);
                }
            }
            result = _.orderBy(result, ['locker']);
            return result;
        },
        lockerInventory() {
            const items = this.inventory.filter(item => {
                return item.locker === this.prev_locker;
            });
            let calculatedItems = {};
            items.forEach(item => {
                let tempId = item['vnr'];
                if (calculatedItems.hasOwnProperty(tempId)) {
                    calculatedItems[tempId]['pills_amount'] += item.pills_amount;
                    calculatedItems[tempId]['amount'] =
                        calculatedItems[tempId]['pills_amount'] / calculatedItems[tempId]['package_size'];
                } else {
                    calculatedItems[tempId] = {
                        amount: item.pills_amount / item.package_size,
                        ...item
                    };
                }
            });
            return calculatedItems;
        },
        tableFields() {
            return [
                { key: 'name', sortable: true, label: this.$t('name'), tdClass: 'align-middle' },
                { key: 'vnr', sortable: true, label: this.$t('vnr'), tdClass: 'align-middle' },
                { key: 'locker', sortable: true, label: this.$t('locker'), tdClass: 'align-middle' },
                {
                    key: 'amount',
                    sortable: true,
                    label: `${this.$t('amount')} [${this.$t('packages_pieces_short')}]`,
                    tdClass: 'align-middle'
                },
                { key: 'last_put_in', sortable: true, label: this.$t('last_put_in'), tdClass: 'align-middle' },
                { key: 'expiry_date', sortable: true, label: this.$t('earliest_expiry_date'), tdClass: 'align-middle' }
            ];
        }
    },
    methods: {
        ...mapActions('account', ['logout']),
        // check if expiry alert should be shown
        checkExpiryAlert(inputExpiryDate) {
            const expiryAlertConfig = this.pharmacy.config.expiry_alert;
            if (!expiryAlertConfig || !expiryAlertConfig.time_unit) {
                return false;
            }
            const expiryDate = new Date(inputExpiryDate);
            const currentDate = new Date();
            currentDate.setUTCHours(0, 0, 0, 0);

            let alertDate = new Date();
            switch (expiryAlertConfig.time_unit) {
                case 'days':
                    alertDate = new Date(expiryDate.setDate(expiryDate.getDate() - expiryAlertConfig.time_value));
                    break;
                case 'weeks':
                    alertDate = new Date(expiryDate.setDate(expiryDate.getDate() - expiryAlertConfig.time_value * 7));
                    break;
                case 'months':
                default:
                    alertDate = new Date(expiryDate.setMonth(expiryDate.getMonth() - expiryAlertConfig.time_value));
                    break;
            }

            const alertDateInFuture = alertDate.valueOf() - currentDate.valueOf() > 0;
            return !alertDateInFuture;
        },
        // handle locker selection
        chooseLocker(num) {
            let el = document.getElementById(this.prev_locker);
            if (el) {
                el.style.width = '180px';
            }
            if (typeof num === 'string') {
                el = document.getElementById(num);
                el.style.width = '130px';
                this.prev_locker = num;
            }
        },
        // get medication display amount
        getDisplayAmount(amount, package_size) {
            const packages = Math.floor(amount);
            const extraMeds = Math.round((amount - packages) * package_size);
            return getMedDisplayAmount(packages, extraMeds, true, null);
        },
        // get low-inventory alert tooltip text
        getLowInvAlertTooltip(vnr) {
            let tooltipText = '';
            if (this.vnrInventory[vnr].inventory <= this.vnrInventory[vnr].lowInvThreshold) {
                tooltipText = `${this.$t('alert_low_inventory')}<br/>(${this.$t('alert_threshold').toLowerCase()}:\xa0${
                    this.vnrInventory[vnr].lowInvThreshold
                })`;
            }
            return tooltipText;
        },
        // get medication's low-inventory alert threshold (either med-specific or pharmacy default)
        getLowInvThreshold(vnr) {
            let threshold = null;
            if (this.pharmacy.medication_alerts && this.pharmacy.medication_alerts.low_inventory) {
                for (const key in this.pharmacy.medication_alerts.low_inventory) {
                    const config = this.pharmacy.medication_alerts.low_inventory[key];
                    if (config.vnr === vnr && config.amount_value) {
                        threshold = config.amount_value;
                        break;
                    }
                }
            }
            if (
                !threshold &&
                this.pharmacy.config.low_inventory_alert &&
                this.pharmacy.config.low_inventory_alert.amount_value
            ) {
                threshold = this.pharmacy.config.low_inventory_alert.amount_value;
            }
            return threshold;
        },
        // get pharmacy info
        async getPharmacy() {
            try {
                this.pharmacy = await getPharmacy(this.user.pharmacy_id);
                const inventory = this.pharmacy.inventory;

                // format inventory object (key format: [vnr + locker])
                for (const key in inventory) {
                    const item = inventory[key];
                    this.inventory.push(item);
                    let vnrLockerId = item.vnr + item.locker;
                    if (this.bruteInventory.hasOwnProperty(vnrLockerId)) {
                        this.bruteInventory[vnrLockerId]['pills_amount'] += item.pills_amount;
                        this.bruteInventory[vnrLockerId]['amount'] =
                            this.bruteInventory[vnrLockerId]['pills_amount'] /
                            this.bruteInventory[vnrLockerId]['package_size'];
                        if (item.created_at > this.bruteInventory[vnrLockerId]['last_put_in']) {
                            this.bruteInventory[vnrLockerId]['last_put_in'] = item.created_at;
                        }
                        if (item.expiry_date < this.bruteInventory[vnrLockerId]['expiry_date']) {
                            this.bruteInventory[vnrLockerId]['expiry_date'] = inventory[key]['expiry_date'];
                            this.bruteInventory[vnrLockerId]['expiry_alert'] = this.checkExpiryAlert(item.expiry_date);
                        }
                    } else {
                        this.bruteInventory[vnrLockerId] = {
                            amount: item.pills_amount / item.package_size,
                            expiry_alert: this.checkExpiryAlert(item.expiry_date),
                            last_put_in: item.created_at,
                            ...item
                        };
                    }
                }
                this.pharmacy.box[0].shift();

                for (let i in this.bruteInventory) {
                    const currentVnr = this.bruteInventory[i]['vnr'];
                    if (!this.vnrInventory[currentVnr]) {
                        // calculate medication's total inventory across lockers
                        const currentVnrInventory = _.filter(this.bruteInventory, { vnr: currentVnr }).reduce(
                            (acc, x) => (acc = acc + x.amount),
                            0
                        );

                        const lowInvThreshold = this.getLowInvThreshold(currentVnr);
                        this.vnrInventory[currentVnr] = {
                            inventory: currentVnrInventory,
                            lowInvThreshold
                        };
                    }
                }
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // switch tab
        switchTab(tabName) {
            this.prev_locker = null;
            for (let tab in this.tabs) {
                this.tabs[tab] = tab === tabName;
            }
        }
    }
};
</script>
